import { useInfiniteQuery } from '@tanstack/react-query';
import { request } from 'javascripts/services/v3ApiClient';
import queryString from 'query-string';

import type {
  V3ApiSuccessData,
  V3ApiError,
} from 'javascripts/services/v3ApiClient';
import type { ProjectDatabaseId } from 'data/v1';
import { ShowcaseProjectMediaItem } from '../types';

export type GetShowcaseProjectMediaResponse = V3ApiSuccessData<{
  filters: {
    capturedAfter: string;
    capturedBefore: string;
  };
  items: ShowcaseProjectMediaItem[];
}>;

export const SHOWCASE_PROJECT_MEDIA_QUERY_KEY = 'ShowcaseProjectMedia';

type ShowcaseProjectMediaParams = {
  shared?: boolean;
  items?: number;
  assetType?: 'image' | 'video';
};

type UseGetShowcaseProjectMediaArgs = {
  projectDatabaseId: ProjectDatabaseId;
  params?: ShowcaseProjectMediaParams;
};

export const useGetShowcaseProjectMedia = ({
  projectDatabaseId,
  params,
}: UseGetShowcaseProjectMediaArgs) => {
  const queryKey = [
    SHOWCASE_PROJECT_MEDIA_QUERY_KEY,
    projectDatabaseId,
    params,
  ];

  return useInfiniteQuery<GetShowcaseProjectMediaResponse, V3ApiError>({
    queryFn: async ({ pageParam }) => {
      const urlQueryString = queryString.stringify({
        ...params,
        ...(pageParam && { after: pageParam }),
      });

      const response = await request({
        url: `projects/${projectDatabaseId}/showcase_project/media${
          urlQueryString && `?${urlQueryString}`
        }`,
      });
      return response.data;
    },
    queryKey,
    getNextPageParam: (lastPage) => {
      if (lastPage?.meta?.hasNextPage) {
        return lastPage?.meta?.nextPage;
      }
      return undefined;
    },
    initialPageParam: '',
  });
};

export type GetShowcaseProjectMediaQueryResult = ReturnType<
  typeof useGetShowcaseProjectMedia
>;
